import angular from 'angular';
import dayjs from 'dayjs';
import Utils from '~/scripts/services/utils';

import APIV5 from '~/models/api';

import logo from '~/images/logo.png';

import templateModalLoginWorkspaces from './modal/modal-login-workspaces.html';
import ModalLoginWorkspacesController from './modal/modal-login-workspaces.controller';

import templateModalLoginConfirmResetPassword from './modal/modal-login-confirm-reset-password.html';
import ModalLoginConfirmResetPasswordController from './modal/modal-login-confirm-reset-password.controller';

import templateModalLoginPasswordResetMail from './modal/modal-login-password-reset-mail.html';
import ModalLoginPasswordResetMailController from './modal/modal-login-password-reset-mail.controller';

/* globals Knitiv */

/**
 * @param {import('@knitiv/api-client-javascript').AuthenticationObject
 * 	 & ?{config: import('@knitiv/api-client-javascript').Config}} context
 * @param {string} url
 * @param {string} version
 */
const setAPI = (context, url, version) => {
	if (context?.url) {
		APIV5.instance.setBackEndpoint(context?.url.back ?? '/');
		APIV5.instance.setFileEndpoint(context?.url.file ?? '/');
	}
	else if (context?.config) {
		APIV5.instance.setBackEndpoint(context?.config.BACK?.url ?? '/');
		APIV5.instance.setFileEndpoint(context?.config.FILE?.url ?? '/');
	}
	APIV5.instance.setToken(context.token.id);
	APIV5.instance.setFrontURL(url);
	APIV5.instance.setFrontVersion(version);
};

export default class LoginCtrl {
	/* @ngInject */
	/**
	 * @param {import('angular').ILocationService} $location
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').route.IRouteParamsService} $routeParams
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {import('angular').IWindowService} $window
	 * @param {any} fromMail
	 * @param {any} Notification
	 * @param {any} User
	 */
	constructor(
		$location,
		$rootScope,
		$routeParams,
		$timeout,
		$translate,
		$uibModal,
		$window,
		fromMail,
		Notification,
		User,
	) {
		this.$location = $location;
		this.$rootScope = $rootScope;
		this.$routeParams = $routeParams;
		this.$translate = $translate;
		this.$timeout = $timeout;
		this.$location = $location;
		this.$uibModal = $uibModal;
		this.$window = $window;
		this.fromMail = fromMail;
		this.Notification = Notification;
		this.User = User;

		this.username = undefined;
		this.password = undefined;
		this.jwt = undefined;
		this.showLogin = false;
		this.showPass = false;
		this.loading = true;
		this.loginLogo = '';
		this.logging = false;

		/** @type {Knitiv.GetConfigOutput['auth_method']} */
		this.authMethods = [];

		if ($rootScope.sso === undefined) {
			$rootScope.sso = false;
		}

		/* Theme */
		/** @type {Knitiv.AppConfigTheme | undefined} */
		const theme = this.$rootScope.appConfig?.theme || undefined;
		this.loginLogo = theme?.logo ?? logo;
		this.buttonStyle = {
			background: theme?.form?.submitButtonColor ?? undefined,
		};
		this.formStyle = {
			background: theme?.form?.background ?? undefined,
		};

		// example /login/_K1_LCB0EDC9D9B1A1C5D5F1EC8949A1254D1DF4DCD8D4ECECE4F051691DD001D81545655525
		if ($routeParams.redirectTo) {
			$rootScope.redirectTo = $routeParams.redirectTo;
		}

		if ($routeParams.redirect) {
			fromMail.push(Utils.k1Decode($routeParams.redirect));
		}

		if ($routeParams.jwt) {
			this.username = undefined;
			this.password = undefined;
			this.jwt = $routeParams.jwt;
			this.logging = true;
			(async() => this.login(true))();
		}
		else if ($routeParams.error && $routeParams.error_description) {
			let message = `<b>${$routeParams.error}</b>&nbsp;: ${$routeParams.error_description}`;
			if ($routeParams.error_uri) {
				message += `<br><a href="${$routeParams.error_uri}">Plus de détails</a>`;
			}
			if ($routeParams.state) {
				message += `<br>${$routeParams.state}`;
			}
			if ($routeParams.file && $routeParams.line) {
				message += `<br><samp>${$routeParams.file}:${$routeParams.line}</samp>`;
			}
			this.Notification.error({
				title:   this.$translate.instant('error.title'),
				message,
			});
		}

		// on redirige l'user vers la page si il y un redirect.
		if (fromMail.is()) {
			(async () => {
				const redirect = await fromMail.pop();
				$location.url(redirect);
			})();
		}

		$rootScope.userPassChange = false;

		if ('logout' in $location.search() && $rootScope.sso) {
			$location.search({ logout: null })
				.replace();
			this.loading = false;
		}

		(async () => {
			this.$rootScope.appConfig = await APIV5.instance.getConfig({}, { id: 'login.constructor' });
			this.$timeout(() => {
				this.authMethods = this.$rootScope.appConfig.auth_method;
				this.showLogin = this.$rootScope.appConfig.showLogin ?? true;

				if (this.authMethods.length === 0) {
					this.showLogin = true;
				}
			});
		})();

		this.loading = false;
	}

	/**
	 * @param {any} auth
	 */
	auth(auth) {
		if (auth?.path) {
			this.$window.location.replace(`/${auth.path}`);
		}
		else if (auth?.url) {
			this.$window.location.replace(auth.url);
		}
	}

	async forgetpass() {
		if (this.username?.trim() === '' || !this.username) {
			this.Notification.error({
				title:   this.$translate.instant('error.title'),
				message: this.$translate.instant('error.missing-login'),
			});
		}

		try {
			await this.$uibModal.open({
				animation:       true,
				ariaLabelledBy:  'modal-confirm-password-title',
				ariaDescribedBy: 'modal-confirm-password-body',
				template:        templateModalLoginConfirmResetPassword,
				controller:      ModalLoginConfirmResetPasswordController,
				controllerAs:    '$ctrl',
				resolve:         {
					username: () => this.username,
				},
			}).result;

			const output = await APIV5.instance.passForgotten({ username: this.username }, { id: 'login.forgetpass' });
			if (output?.error === null) {
				this.$uibModal.open({
					animation:       true,
					ariaLabelledBy:  'modal-password-reset-mail-title',
					ariaDescribedBy: 'modal-password-reset-mail-body',
					template:        templateModalLoginPasswordResetMail,
					controller:      ModalLoginPasswordResetMailController,
					controllerAs:    '$ctrl',
					resolve:         {
						username:        () => this.username,
						delayExpiration: () => output.delay_expiration,
						delayUnit:       () => output.delay_unit,
					},
				});
			}
			else {
				this.Notification.error({
					title:   this.$translate.instant('error.login-invalid'),
					message: this.$translate.instant('error.contact-admin'),
				});
				angular.element('#username')
					.trigger('focus');
			}
		}
		catch (error) {
			if (error !== 'cancel') {
				console.error(error);
			}
		}
	}

	/**
	 * @param {boolean} [auto]
	 * @returns {Promise<any>} Promise result
	 */
	async login(auto = false) {
		// event.preventDefault();
		if (!auto && (!this.username || !this.password)) {
			this.Notification.error({
				title:   this.$translate.instant('error.title'),
				message: this.$translate.instant(`error.${this.username ? 'password-empty' : 'login-empty'}`),
			});
		}
		this.showPass = false;
		this.loading = true;

		try {
			const result = this.jwt
				? await APIV5.instance.workspaceList({
					jwt: this.jwt,
				}, { id: 'login.login-jwt' })
				: await APIV5.instance.workspaceList({
					username: this.username,
					password: this.password,
				}, { id: 'login.login-username-password' });

			if (result) {
				const session = {
					...result,
					username: this.username,
					password: this.password,
				};

				if (
					!session.workspaces
					|| session.workspaces.info.count_env === 0
					|| session.workspaces.info.total_count_database === 0
				) {
					this.loading = false;

					if ((!auto && (this.username || this.password)) || session.auth === 'Negotiate') {
						this.Notification.error({
							title:   this.$translate.instant('error.title'),
							message: this.$translate.instant('error.no-database'),
						});
					}
					else {
						const search = this.$location.search();

						if (search.logout) {
							search.logout = null;
						}

						if (search.error) {
							search.error = null;
						}

						this.$location.search(search);
					}
				}
				else if (session.workspaces.info.total_count_database === 1) {
					const databaselist = Array.isArray(session.workspaces.env_list[session.env_kid].database_list)
						? session.workspaces.env_list[session.env_kid].database_list
						: Object.values(session.workspaces.env_list[session.env_kid].database_list);

					this.logging = true;
					// eslint-disable-next-line promise/catch-or-return,promise/no-nesting
					await this.userSet(session, {
						env:      session.env_kid,
						database: databaselist[0],
						// eslint-disable-next-line promise/always-return
					});
				}
				else {
					this.logging = true;
					const workspace = await this.$uibModal.open({
						animation:       true,
						ariaLabelledBy:  'modal-login-workspaces-title',
						ariaDescribedBy: 'modal-login-workspaces-body',
						template:        templateModalLoginWorkspaces,
						controller:      ModalLoginWorkspacesController,
						controllerAs:    '$ctrl',
						resolve:         {
							admin:      () => result.profil_admin === 1,
							workspaces: () => session.workspaces,
						},
					}).result;

					this.$rootScope.title = '';
					await this.userSet(session, workspace);
					// eslint-disable-next-line promise/always-return
					if (this.fromMail.is()) {
						// eslint-disable-next-line promise/catch-or-return,promise/no-nesting
						const redirect = await this.fromMail.pop();
						this.$location.url(redirect);
					}
				}
			}
			else {
				this.loading = false;
				this.logging = false;
			}
		}
		catch (error) {
			console.error('login error', error);
			this.loading = false;
			this.logging = false;
			this.$rootScope.title = '';
			if (error !== 'cancel' && error !== 'backdrop click') {
				console.error('modalInstance.result error', error);
			}
		}
	}

	loginSSO() {
		this.username = undefined;
		this.password = undefined;
	}

	/**
	 * @param {any} user
	 * @param {any} workspace
	 */
	async userSet(user, workspace) {
		this.username = user.username;
		this.password = user.password;
		this.loading = true;

		if (!user.username && !user.password) {
			this.$rootScope.sso = true;
		}

		// Remove notification messages
		if (this.$rootScope.scopeNotification) {
			this.$rootScope.scopeNotification.kill();
		}

		/** @type {import('@knitiv/api-client-javascript').TimezoneLogin} */
		const tz = {
			name:   dayjs.tz.guess(),
			offset: dayjs().utcOffset(),
		};

		try {
			const loginRequest = {
				clientid: user._know_,
				jwt:      this.jwt,
				dsnasso:  workspace.database.dsnasso_kid,
				password: user.password,
				tz,
				username: user.username,
			};

			if (workspace.date) {
				// @ts-ignore
				loginRequest.date = {
					target: workspace.date,
				};
			}

			/** @type {any} */
			const loginResponse = await APIV5.instance.login(loginRequest, { id: 'login.user-set' });

			if (loginResponse) {
				setAPI(loginResponse, this.$location.url(), this.$rootScope.tag);
				this.User.set(loginResponse)
					.saveToken();

				this.$rootScope.appConfig = await APIV5.instance.getConfig({}, { id: 'login.user-set' });
				this.$timeout(() => {
					if (loginResponse.passchange === 1) {
						this.$rootScope.userPassChange = true;
						this.$location.path('/passchange');
					}
					else {
						this.$rootScope.$emit('User.login');
						this.$rootScope.goToPreviousURL();
					}
					this.loading = false;
				});
			}
			else {
				this.$timeout(() => {
					this.loading = false;
				});
			}
		}
		catch (error) {
			this.loading = false;
			this.logging = false;
			this.Notification.error({
				title:   this.$translate.instant('error.title'),
				message: Utils.encodeHTMLEntities(error?.toString()),
			});
		}
	}
}
